import {
  ITERABLE_BUTTON_PARENT_CLASS,
  ITERABLE_CARD_ITEM_WRAPPER_CLASSES,
  ITERABLE_CONTENT_CLASS,
  ITERABLE_CONTENT_WRAPPER_CLASSES,
  ITERABLE_IMAGE_INNER_WRAPPER_CLASSES,
  ITERABLE_IMAGE_WRAPPER_BANNER_CLASSES,
  ITERABLE_IMAGE_WRAPPER_CLASS,
  STYLE_MAP,
  TEXT_SELECTORS,
  type MessageType
} from '~/utils/iterable-placement-styles'

export function getParentDiv (element: HTMLElement | null): HTMLElement | null {
  const parentElement = element?.parentElement
  return (parentElement?.tagName === 'DIV' ? parentElement : parentElement?.closest?.('div') ?? null) ?? null
}

export interface AddClassToIterableNodeOptions {
  /**
   * When true (default), clears the element’s inline `style` so layout classes win over Iterable SDK defaults.
   * Set false if the node may carry intentional inline styles from outside this pass.
   */
  stripInlineStyle?: boolean
}

/**
 * Adds space-separated / multi-arg classes to an element inside Iterable embedded output.
 * No-ops when every class string is empty. By default strips inline `style` (Iterable-heavy); opt out via trailing options.
 */
export function addClassAndRemoveStyle (
  element: HTMLElement | null,
  ...parts: (string | AddClassToIterableNodeOptions)[]
): void {
  if (!element) return
  let stripInlineStyle = true
  const classStringChunks: string[] = []
  for (const part of parts) {
    if (part != null && typeof part === 'object' && !Array.isArray(part) && 'stripInlineStyle' in part) {
      const options = part as AddClassToIterableNodeOptions
      if (options.stripInlineStyle === false) stripInlineStyle = false
    } else if (typeof part === 'string') {
      classStringChunks.push(part)
    }
  }
  const classTokens = classStringChunks.flatMap((classString) => classString.split(/\s+/).filter(Boolean))
  if (classTokens.length === 0) return
  for (const classToken of classTokens) {
    element.classList.add(classToken)
  }
  if (stripInlineStyle) {
    element.removeAttribute('style')
  }
}

function sortElementsDomOrder (elements: HTMLElement[]): HTMLElement[] {
  return [...elements].sort((left, right) => {
    const position = left.compareDocumentPosition(right)
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1
    if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1
    return 0
  })
}

/**
 * Banner layout: move button row into content wrapper and image beside content when those nodes exist.
 * Any subset may be missing (Iterable omits title/body/buttons/image); only perform moves when safe.
 */
function rearrangeBannerStructure (placementItem: Element): void {
  const bannerRoot = placementItem.querySelector<HTMLElement>(':scope > *:first-child')
  if (!bannerRoot) return

  const contentBlock = placementItem.querySelector<HTMLElement>(`.${ITERABLE_CONTENT_CLASS}`)
  const contentWrapper = contentBlock
    ? contentBlock.parentElement?.tagName === 'DIV'
      ? contentBlock.parentElement
      : contentBlock.parentElement?.closest?.('div') ?? null
    : null

  const buttonParent = placementItem.querySelector<HTMLElement>(`.${ITERABLE_BUTTON_PARENT_CLASS}`)
  const imageWrapper = placementItem.querySelector<HTMLElement>(`.${ITERABLE_IMAGE_WRAPPER_CLASS}`)

  if (contentWrapper && buttonParent && buttonParent.parentElement !== contentWrapper) {
    contentWrapper.appendChild(buttonParent)
  }

  if (
    imageWrapper &&
    bannerRoot &&
    contentWrapper &&
    imageWrapper.parentElement === contentWrapper
  ) {
    bannerRoot.appendChild(imageWrapper)
  }
}

export interface ApplyIterablePlacementDomOptions {
  messageType: MessageType
  /** Banner only: desktop image side; `right` matches existing default layout. */
  bannerImageDesktopPlacement?: 'left' | 'right'
  /** When the parent already provides grid columns + `.card-program-item`, skip inner `ITERABLE_CARD_ITEM_WRAPPER_CLASSES`. */
  omitCardItemWrapper?: boolean
  buttonClasses: string[]
  titleClass: string
  bodyClass: string
  stopMutationObserver: () => void
  resumeMutationObserver: () => void
  logError: (context: string, err: unknown) => void
}

/**
 * Applies layout classes and DOM structure to Iterable SDK HTML under `.iterable-embedded-placement`.
 * Call from client only. Observer should be paused around banner/card mutations via options.
 */
export function applyIterablePlacementDom (
  placementRoot: HTMLElement,
  options: ApplyIterablePlacementDomOptions
): void {
  if (!placementRoot?.querySelectorAll) return

  const messageType = options.messageType
  const messageTypeStyles = STYLE_MAP[messageType]
  const {
    bannerImageDesktopPlacement = 'right',
    omitCardItemWrapper = false,
    buttonClasses,
    titleClass,
    bodyClass,
    stopMutationObserver,
    resumeMutationObserver,
    logError
  } = options

  placementRoot.querySelectorAll('.iterable-embedded-item').forEach((embeddedItem) => {
    const itemOuterWrapper = embeddedItem.querySelector<HTMLElement>(':scope > *:first-child')
    if (!itemOuterWrapper) return
    addClassAndRemoveStyle(itemOuterWrapper, messageTypeStyles.parent)
    // Banner desktop image side: never stack left+right (idempotent; clears stale when type/options change).
    itemOuterWrapper.classList.remove('image-position-left', 'image-position-right')
    if (messageType === 'banner') {
      const positionClass =
        bannerImageDesktopPlacement === 'left' ? 'image-position-left' : 'image-position-right'
      addClassAndRemoveStyle(itemOuterWrapper, positionClass)
    }

    const removeNuxtInjectedImages = () => {
      embeddedItem.querySelectorAll<HTMLImageElement>('img[data-iterable-nuxt-media="1"]').forEach((el) => {
        el.remove()
      })
    }

    if (messageType === 'notification') {
      const itemEl = embeddedItem as HTMLElement
      const mediaUrl = itemEl.dataset.iterableMediaUrl?.trim()
      removeNuxtInjectedImages()
      if (mediaUrl && embeddedItem.querySelectorAll('img').length === 0) {
        const injected = document.createElement('img')
        injected.src = mediaUrl
        injected.alt = ''
        injected.loading = 'lazy'
        injected.setAttribute('data-iterable-nuxt-media', '1')
        itemOuterWrapper.insertBefore(injected, itemOuterWrapper.firstChild)
      }
    } else {
      removeNuxtInjectedImages()
    }

    const titleAndBodyElements = Array.from(embeddedItem.querySelectorAll<HTMLElement>(TEXT_SELECTORS))
    const titleAndBodyClasses = [titleClass, bodyClass]
    titleAndBodyElements.slice(0, 2).forEach((textElement, slotIndex) => {
      if (textElement && titleAndBodyClasses[slotIndex]) {
        textElement.className = titleAndBodyClasses[slotIndex]
        // Iterable SDK often inlines typography; drop so our classes apply. Co-located markup only.
        textElement.removeAttribute('style')
      }
    })
    if (titleAndBodyElements.length > 0) {
      addClassAndRemoveStyle(getParentDiv(titleAndBodyElements[0]), ITERABLE_CONTENT_CLASS)
    }

    const images = Array.from(embeddedItem.querySelectorAll<HTMLElement>('img'))
    images.forEach((imageElement) => {
      if (!imageElement?.isConnected) return
      // Iterable-generated <img> sizing; placement subtree only.
      imageElement.removeAttribute('style')
      if (messageTypeStyles.image) imageElement.classList.add(messageTypeStyles.image)
      if (!imageElement.closest(`.${ITERABLE_IMAGE_WRAPPER_CLASS}`)) {
        const imageOuterWrapper = document.createElement('div')
        imageOuterWrapper.className = ITERABLE_IMAGE_WRAPPER_CLASS
        imageElement.parentElement?.insertBefore(imageOuterWrapper, imageElement)
        imageOuterWrapper.appendChild(imageElement)
      }
    })

    const callToActionElements = Array.from(
      embeddedItem.querySelectorAll<HTMLAnchorElement | HTMLButtonElement>('a[href], button')
    )
    callToActionElements.slice(0, 2).forEach((actionElement, index) => {
      actionElement.className =
        buttonClasses?.[index] ?? (index === 0 ? messageTypeStyles.primaryButton : messageTypeStyles.secondaryButton)
      // Iterable button/link inline styles; placement subtree only.
      actionElement.removeAttribute('style')
    })
    if (callToActionElements.length > 0) {
      addClassAndRemoveStyle(getParentDiv(callToActionElements[0]), ITERABLE_BUTTON_PARENT_CLASS)
    }

    if (messageType === 'banner') {
      stopMutationObserver()
      try {
        const contentBlock = embeddedItem.querySelector<HTMLElement>(`.${ITERABLE_CONTENT_CLASS}`)
        const bannerRoot = embeddedItem.querySelector<HTMLElement>(':scope > *:first-child')
        const contentWrapper = contentBlock ? getParentDiv(contentBlock) : null
        if (contentBlock && contentWrapper && bannerRoot) {
          if (contentWrapper === bannerRoot) {
            const contentBlockWrapper = document.createElement('div')
            ITERABLE_CONTENT_WRAPPER_CLASSES.split(/\s+/)
              .filter(Boolean)
              .forEach((classToken) => contentBlockWrapper.classList.add(classToken))
            bannerRoot.insertBefore(contentBlockWrapper, contentBlock)
            contentBlockWrapper.appendChild(contentBlock)
          } else {
            addClassAndRemoveStyle(contentWrapper, ITERABLE_CONTENT_WRAPPER_CLASSES)
          }
        }
        rearrangeBannerStructure(embeddedItem)
        embeddedItem.querySelectorAll<HTMLElement>(`.${ITERABLE_IMAGE_WRAPPER_CLASS}`).forEach((imageWrapper) => {
          addClassAndRemoveStyle(imageWrapper, ITERABLE_IMAGE_WRAPPER_BANNER_CLASSES)
        })
        Array.from(embeddedItem.querySelectorAll<HTMLElement>(`.${ITERABLE_IMAGE_WRAPPER_CLASS} img`)).forEach(
          (imageElement) => {
            try {
              const imageParent = imageElement.parentElement
              if (!imageParent || !imageElement.isConnected || imageParent.classList.contains('image-wrapper')) {
                return
              }
              const imageInnerWrapper = document.createElement('div')
              ITERABLE_IMAGE_INNER_WRAPPER_CLASSES.split(/\s+/)
                .filter(Boolean)
                .forEach((classToken) => imageInnerWrapper.classList.add(classToken))
              imageParent.insertBefore(imageInnerWrapper, imageElement)
              imageInnerWrapper.appendChild(imageElement)
            } catch (err) {
              logError('image-wrapper insert', err)
            }
          }
        )
      } finally {
        resumeMutationObserver()
      }
    }

    if (messageType === 'card' && !omitCardItemWrapper) {
      stopMutationObserver()
      try {
        const cardOuterWrapper = itemOuterWrapper
        if (cardOuterWrapper?.classList.contains('iterable-card-parent')) {
          const existingCardProgramItem = cardOuterWrapper.querySelector(':scope > .card-program-item')
          if (!existingCardProgramItem) {
            const cardSectionElements = Array.from(
              cardOuterWrapper.querySelectorAll<HTMLElement>(
                `.${ITERABLE_IMAGE_WRAPPER_CLASS}, .${ITERABLE_CONTENT_CLASS}, .${ITERABLE_BUTTON_PARENT_CLASS}`
              )
            )
            const orderedCardElements = sortElementsDomOrder(
              cardSectionElements.filter((element) => element?.isConnected)
            )
            const firstInDocumentOrder = orderedCardElements[0]
            if (firstInDocumentOrder) {
              const cardItemWrapper = document.createElement('div')
              ITERABLE_CARD_ITEM_WRAPPER_CLASSES.split(/\s+/)
                .filter(Boolean)
                .forEach((classToken) => cardItemWrapper.classList.add(classToken))
              cardOuterWrapper.insertBefore(cardItemWrapper, firstInDocumentOrder)
              orderedCardElements.forEach((element) => cardItemWrapper.appendChild(element))
            }
          }
        }
      } finally {
        resumeMutationObserver()
      }
    }
  })
}
